import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Shield, Clock, AlertTriangle, XOctagon, XCircle, CheckCircle, Star } from "lucide-react";

const VERIFY_COPY = {
  ACTIVE: { icon: Shield, color: "text-lv-active", heading: "Valid LV Pass", subtitle: "This membership is active." },
  PENDING: { icon: Clock, color: "text-bronze", heading: "Membership pending", subtitle: "This membership has not yet been activated." },
  SUSPENDED: { icon: AlertTriangle, color: "text-lv-inactive", heading: "Pass currently inactive", subtitle: "This membership is currently suspended." },
  EXPIRED: { icon: XOctagon, color: "text-lv-inactive", heading: "Membership expired", subtitle: "This membership is no longer active." },
  INVALID: { icon: XCircle, color: "text-lv-muted", heading: "Pass not recognized", subtitle: "We could not verify this credential." },
};

const DESIGNATION_LABEL = { FOUNDING_HOME_CLUB: "Founding Home Club", LV_PARTNER: "LV Partner", LV_GLOBAL_PARTNER: "LV Global Partner" };

export default function Verify() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get("partner");

  const { data, isLoading, error } = useQuery({
    queryKey: ["verify", token, partnerId],
    queryFn: () => api.get(`/verify/${token}${partnerId ? `?partner=${partnerId}` : ""}`),
    refetchOnMount: "always", staleTime: 0, gcTime: 0,
  });

  if (isLoading) return <div className="min-h-screen bg-lv-bg flex items-center justify-center" data-testid="verify-loading"><div className="w-10 h-0.5 bg-bronze animate-pulse" /></div>;
  if (error) return (
    <div className="min-h-screen bg-lv-bg flex items-center justify-center px-6" data-testid="verify-error">
      <div className="text-center"><XCircle size={40} className="text-lv-muted mx-auto mb-4" /><p className="text-cream text-lg font-serif">Verification unavailable</p><p className="text-lv-muted text-sm mt-2">Please try again in a moment.</p></div>
    </div>
  );

  const state = data?.state || "INVALID";
  const cfg = VERIFY_COPY[state] || VERIFY_COPY.INVALID;
  const Icon = cfg.icon;
  const partnerData = data?.partner;

  return (
    <div className="min-h-screen bg-lv-bg flex flex-col items-center justify-center px-6 py-12" data-testid="verify-page">
      <div className="mb-8 text-center">
        <span className="font-serif text-lg sm:text-xl text-cream tracking-[0.25em]">LV PASS</span>
        <p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted mt-1">Credential Verification</p>
      </div>

      <div className="w-full max-w-sm overflow-hidden" style={{ background: "linear-gradient(180deg, #3B2A1E 0%, #241812 100%)", border: "1px solid #A8794A", borderRadius: "8px", boxShadow: "0 8px 32px rgba(20, 16, 12, 0.8)" }} data-testid="verify-card">
        {state === "ACTIVE" ? (
          <div className="p-6 sm:p-8" data-testid="verify-active">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Shield size={20} className="text-lv-active" />
              <span className="text-xs tracking-[0.3em] uppercase text-lv-active font-medium">Valid LV Pass</span>
            </div>
            <p className="text-center text-xs text-lv-muted tracking-wide mb-6">This membership is active.</p>
            <div className="text-center space-y-4">
              <div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Member</p>
                <p className="font-serif text-xl sm:text-2xl text-cream tracking-wide mt-1">{data.full_name}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Member ID</p>
                <p className="font-mono text-base sm:text-lg text-cream tracking-wider mt-0.5" data-testid="verify-member-code">{data.member_code}</p>
              </div>
              <div className="flex justify-center gap-6 sm:gap-8">
                <div><p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Chapter</p><p className="text-sm text-cream mt-0.5">{data.home_chapter_name}</p></div>
                <div><p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Since</p><p className="text-sm text-cream mt-0.5">{data.member_since ? new Date(data.member_since).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "\u2014"}</p></div>
              </div>
              {data.is_founding_member && (
                <span className="inline-flex items-center px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded-full bg-bronze/10 text-bronze border border-bronze/20" data-testid="verify-founding-badge">Founding Member</span>
              )}
            </div>

            {/* Partner-specific privileges */}
            {partnerData && partnerData.privileges?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-bronze/20">
                <div className="flex items-center gap-2 mb-4">
                  {partnerData.designation === "FOUNDING_HOME_CLUB" && <Star size={14} className="text-bronze" />}
                  <p className="text-xs tracking-[0.2em] uppercase text-lv-muted">{partnerData.name}</p>
                  <span className="text-[9px] text-lv-muted/50 tracking-wider">{DESIGNATION_LABEL[partnerData.designation]}</span>
                </div>
                <div className="space-y-3">
                  {partnerData.privileges.map((pv, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle size={14} className="text-lv-active shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-cream">{pv.title}</p>
                        {pv.how_to_use && <p className="text-xs text-lv-muted mt-0.5">{pv.how_to_use}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 sm:p-8 text-center" data-testid={`verify-${state.toLowerCase()}`}>
            <Icon size={36} className={`${cfg.color} mx-auto mb-4`} />
            <p className="font-serif text-lg sm:text-xl text-cream tracking-wide mb-2">{cfg.heading}</p>
            <p className="text-sm text-lv-muted tracking-wide leading-relaxed">{cfg.subtitle}</p>
          </div>
        )}
      </div>
      <p className="text-[9px] tracking-[0.3em] uppercase text-lv-muted/40 mt-6">Verified {new Date().toLocaleString()}</p>
    </div>
  );
}
